import { AlertTriangle, LockKeyhole, RefreshCw } from './components/icons';
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { AppShell, type ViewId } from './components/AppShell';
import {
  checkAuthStatus,
  loadFinanceData,
  login,
  logout,
  refreshQuickBooks,
  type FinanceLoadResult,
} from './data/financeAdapter';
import { clearFinanceWorkspaceStorage, ScenarioProvider } from './state/ScenarioContext';
import { AssumptionsView } from './views/AssumptionsView';
import { CashView } from './views/CashView';
import { ExecutiveView } from './views/ExecutiveView';
import { ExpenseExplorerView } from './views/ExpenseExplorerView';
import { RevenueView } from './views/RevenueView';
import { ScenarioCompareView } from './views/ScenarioCompareView';
import { AgentView } from './views/AgentView';

const App = (): ReactElement => {
  const [finance, setFinance] = useState<FinanceLoadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginPending, setLoginPending] = useState(false);
  const [refreshPending, setRefreshPending] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [modelRefreshNotice, setModelRefreshNotice] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewId>('executive');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const automaticSyncVersion = useRef<string | null>(null);
  const modelRefreshPending = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setFinance(await loadFinanceData());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load financial data.');
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      try {
        const authenticated = await checkAuthStatus();
        setAuthState(authenticated ? 'authenticated' : 'unauthenticated');
        if (authenticated) await load();
      } catch (authError) {
        setError(authError instanceof Error ? authError.message : 'Unable to verify access.');
      }
    };
    void initialize();
  }, [load]);

  useEffect(() => {
    const expired = () => {
      setFinance(null);
      setAuthState('unauthenticated');
      setRefreshError('Your session expired. Sign in again to continue.');
    };
    window.addEventListener('deverus:session-expired', expired);
    return () => window.removeEventListener('deverus:session-expired', expired);
  }, []);

  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password) return;
    setLoginPending(true);
    setLoginError(null);
    try {
      await login(password);
      setPassword('');
      setAuthState('authenticated');
      await load();
    } catch (loginFailure) {
      setLoginError(loginFailure instanceof Error ? loginFailure.message : 'Unable to sign in.');
    } finally {
      setLoginPending(false);
    }
  };

  const syncQuickBooks = async () => {
    setRefreshPending(true);
    setRefreshError(null);
    try {
      await refreshQuickBooks();
      await load();
    } catch (syncError) {
      setRefreshError(syncError instanceof Error ? syncError.message : 'QuickBooks refresh failed.');
    } finally {
      setRefreshPending(false);
    }
  };

  useEffect(() => {
    if (!finance?.integration.quickBooks.connected || refreshPending) return;
    const version = finance.data.datasetVersion ?? finance.data.lastSyncedAt;
    if (automaticSyncVersion.current === version) return;
    const serverDataTime = Date.parse(finance.data.lastSyncedAt);
    const cookieSyncTime = Date.parse(finance.integration.quickBooks.lastSyncedAt ?? '');
    const needsRecovery = finance.data.dataStatus !== 'live' || (
      Number.isFinite(cookieSyncTime) && (!Number.isFinite(serverDataTime) || serverDataTime + 1000 < cookieSyncTime)
    );
    if (!needsRecovery) return;
    automaticSyncVersion.current = version;
    void syncQuickBooks();
  }, [finance, refreshPending]);

  useEffect(() => {
    if (authState !== 'authenticated' || !finance || finance.mode !== 'authenticated') return;
    const currentVersion = finance.data.datasetVersion ?? finance.data.lastSyncedAt;

    const refreshIfModelChanged = async () => {
      if (modelRefreshPending.current || document.visibilityState === 'hidden') return;
      modelRefreshPending.current = true;
      try {
        const latest = await loadFinanceData();
        const latestVersion = latest.data.datasetVersion ?? latest.data.lastSyncedAt;
        if (latestVersion !== currentVersion) {
          clearFinanceWorkspaceStorage();
          setFinance(latest);
          setModelRefreshNotice('The active workbook or QuickBooks dataset changed. The latest model was loaded and the working scenario was reset.');
        }
      } catch {
        // Keep the currently verified model available if a background freshness check fails.
      } finally {
        modelRefreshPending.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshIfModelChanged();
    };
    const interval = window.setInterval(() => void refreshIfModelChanged(), 30_000);
    window.addEventListener('focus', refreshIfModelChanged);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfModelChanged);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authState, finance]);

  const signOut = async () => {
    try {
      await logout();
    } finally {
      clearFinanceWorkspaceStorage();
      setFinance(null);
      setAuthState('unauthenticated');
    }
  };

  if (error) {
    return (
      <main className="boot-screen">
        <div className="boot-card error-card">
          <span className="boot-icon"><AlertTriangle size={24} /></span>
          <p className="eyebrow">Data connection unavailable</p>
          <h1>We could not load the finance workspace.</h1>
          <p>{error}</p>
          <button className="button primary" onClick={() => window.location.reload()}><RefreshCw size={16} />Try again</button>
        </div>
      </main>
    );
  }

  if (authState === 'unauthenticated') {
    return (
      <main className="boot-screen auth-screen">
        <form className="boot-card login-card" onSubmit={submitLogin}>
          <span className="boot-icon secure"><LockKeyhole size={24} /></span>
          <div>
            <p className="eyebrow">Private finance workspace</p>
            <h1>Sign in to Deverus Finance</h1>
            <p>Owner access unlocks live financial actuals and named payroll. Approved QA credentials open a separate synthetic, read-only sandbox and never expose production finance data.</p>
          </div>
          <label className="login-field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />
          </label>
          {loginError ? <p className="form-error" role="alert">{loginError}</p> : null}
          <button className="button primary login-submit" disabled={loginPending || !password}>
            {loginPending ? <span className="button-spinner" aria-hidden="true" /> : <LockKeyhole size={16} />}
            {loginPending ? 'Signing in…' : 'Sign in securely'}
          </button>
          <small>Protected with a signed, HTTP-only session cookie.</small>
        </form>
      </main>
    );
  }

  if (authState === 'checking' || !finance) {
    return (
      <main className="boot-screen" aria-busy="true">
        <div className="boot-card">
          <span className="loading-ring" aria-hidden="true" />
          <p className="eyebrow">Secure finance workspace</p>
          <h1>Loading the latest actuals and forecast…</h1>
        </div>
      </main>
    );
  }

  const navigate = (view: ViewId) => setActiveView(view);
  const renderView = (): ReactElement => {
    switch (activeView) {
      case 'agent': return <AgentView key={finance.data.datasetVersion ?? finance.data.lastSyncedAt} data={finance.data} integration={finance.integration} onDataActivated={load} />;
      case 'expenses': return <ExpenseExplorerView />;
      case 'assumptions': return <AssumptionsView onNavigate={navigate} decisionReadiness={finance.integration.decisionReadiness} />;
      case 'revenue': return <RevenueView />;
      case 'cash': return <CashView />;
      case 'compare': return <ScenarioCompareView />;
      default: return <ExecutiveView onNavigate={navigate} />;
    }
  };

  return (
    <ScenarioProvider key={finance.data.datasetVersion ?? finance.data.lastSyncedAt} data={finance.data}>
      <AppShell
        data={finance.data}
        mode={finance.mode}
        integration={finance.integration}
        activeView={activeView}
        onViewChange={navigate}
        mobileNavOpen={mobileNavOpen}
        onMobileNavToggle={() => setMobileNavOpen((open) => !open)}
        onQuickBooksRefresh={() => void syncQuickBooks()}
        onLogout={() => void signOut()}
        refreshPending={refreshPending}
        refreshError={refreshError}
        modelRefreshNotice={modelRefreshNotice}
      >
        {renderView()}
      </AppShell>
    </ScenarioProvider>
  );
};

export default App;
